import { useRef } from "react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import PartnerForm from "@/components/booking/PartnerForm";
import { PartnerCard } from "@/components/fedari/Partners";
import { Reveal, SectionHead } from "@/components/fedari/Reveal";
import { PARTNERS } from "@/data/partners";
import { breadcrumbSchema } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { ArrowRight, UserCheck, FileCheck, Repeat, Building2, Briefcase, ScrollText, Landmark } from "lucide-react";

const BENEFITS = [
  { icon: UserCheck, text: "One dedicated point of contact." },
  { icon: FileCheck, text: "Independent inspection reports with photographic findings." },
  { icon: Repeat, text: "Tailored arrangements for recurring referrals." },
];

const AUDIENCES = [
  { icon: Building2, text: "Real estate agencies." },
  { icon: Briefcase, text: "Property management companies." },
  { icon: ScrollText, text: "Conveyancing firms." },
  { icon: Landmark, text: "Corporate and property service businesses." },
];

export default function Partners() {
  const path = "/partners";
  const partnersRef = useRef(null);
  const formRef = useRef(null);
  const scrollTo = (ref, action) => {
    trackEvent("partners_cta", { action });
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell breadcrumbs={[{ name: "Partners", path }]}>
      {() => (
        <>
          <Seo
            title="Partners | Fedari Property Inspection UAE"
            description="Explore Fedari's partner network and enquire about collaboration for independent property inspections across Dubai and the UAE."
            path={path}
            jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Partners", path }])]}
          />

          {/* Navy hero */}
          <section className="bg-navy text-white py-16 lg:py-24" data-testid="partners-hero">
            <div className="wrap max-w-[860px]">
              <span className="font-mono-jb text-[0.68rem] tracking-[0.18em] uppercase text-[#7FB2FF]">The Fedari Partner Network</span>
              <h1 className="text-[clamp(2rem,4.4vw,3.2rem)] leading-[1.08] tracking-[-0.03em] text-white mt-3 mb-4">Better together. For every property.</h1>
              <p className="text-[1.05rem] text-[#C6D6EC] max-w-[54ch] mb-8">Connect your clients with independent property inspections. Build a partnership around clear reporting, personal service and confident handovers.</p>
              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <button onClick={() => scrollTo(formRef, "become-a-partner")} data-testid="partners-hero-cta"
                  className="inline-flex items-center justify-center gap-2 bg-blue text-white px-7 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                  Become a Partner <ArrowRight size={16} />
                </button>
                <button onClick={() => scrollTo(partnersRef, "meet-our-partners")} data-testid="partners-hero-secondary"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-[10px] border border-white/25 text-white font-semibold hover:border-white/60 transition-colors">
                  Meet Our Partners
                </button>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {BENEFITS.map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3.5">
                    <Icon size={18} className="text-[#7FB2FF] flex-none mt-0.5" />
                    <span className="text-[0.88rem] text-[#E6EEF8] leading-snug">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Our partners */}
          <section ref={partnersRef} id="our-partners" className="py-16 lg:py-20 bg-white scroll-mt-[130px] lg:scroll-mt-[190px]" data-testid="partners-list">
            <div className="wrap">
              <SectionHead center eyebrow="Partner network" title="Our Partners"
                sub="Meet the businesses in Fedari's partner network." />
              <div className="grid sm:grid-cols-2 gap-5 max-w-[820px] mx-auto">
                {PARTNERS.map((p, i) => (
                  <Reveal key={p.name} delay={i * 0.06}><PartnerCard partner={p} /></Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Become a partner */}
          <section ref={formRef} id="become-a-partner" className="py-16 lg:py-20 bg-soft border-t border-line scroll-mt-[130px] lg:scroll-mt-[190px]" data-testid="become-a-partner">
            <div className="wrap grid lg:grid-cols-[minmax(0,420px),1fr] gap-10 lg:gap-16 items-start max-w-[1080px]">
              <div>
                <span className="eyebrow">Collaborate</span>
                <h2 className="text-[clamp(1.7rem,3vw,2.3rem)] mt-3 mb-4">Become a Fedari Partner</h2>
                <p className="text-bodytext mb-6">Work with Fedari to support your clients with independent property inspections. Tell us about your business and how you would like to collaborate.</p>
                <ul className="space-y-2.5">
                  {AUDIENCES.map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-center gap-2.5 text-bodytext text-[0.95rem]">
                      <Icon size={17} className="text-blue flex-none" /> {text}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border border-line rounded-2xl p-6 lg:p-8">
                <PartnerForm />
              </div>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
